import React, { useEffect, useState } from 'react';
import { api, type HousekeepingLogEntry } from '../lib/api';
import { fmtClock } from '../lib/format';
import { OperaWindow, OperaSection } from './OperaWindow';

interface Props {
  roomId: string;
  onClose: () => void;
}

function statusBadge(s: string | null) {
  if (!s) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const colour = s === 'CLEAN' ? 'var(--accent-green)' : 'var(--accent-amber)';
  return <span style={{ color: colour, fontWeight: 600 }}>{s === 'CLEAN' ? '✓ Clean' : '⚠ Dirty'}</span>;
}

export function HousekeepingLogModal({ roomId, onClose }: Props) {
  const [logs, setLogs] = useState<HousekeepingLogEntry[]>([]);
  const [roomLabel, setRoomLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.housekeepingLog(roomId);
        setLogs(r.logs);
        setRoomLabel(r.room.displayName
          ? `Room ${r.room.number} — ${r.room.displayName}`
          : `Room ${r.room.number}`);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [roomId]);

  return (
    <OperaWindow
      title="HOUSEKEEPING LOG"
      subtitle={roomLabel}
      onClose={onClose}
      size="large"
      sideButtons={[{ label: 'Close', onClick: onClose }]}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Recent activity">
        {loading && <div className="loading">Loading…</div>}
        {!loading && logs.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            No history recorded yet.
          </div>
        )}
        {!loading && logs.length > 0 && (
          <table className="hskp-log-table">
            <thead>
              <tr>
                <th>When</th>
                <th>From</th>
                <th>To</th>
                <th>By</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id}>
                  <td>{fmtClock(l.performedAt)}</td>
                  <td>{statusBadge(l.previousStatus)}</td>
                  <td>{statusBadge(l.newStatus)}</td>
                  <td>{l.performedBy}</td>
                  <td>{l.note || <span style={{ color: 'var(--text-secondary)' }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </OperaSection>

      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
        Showing last {logs.length} status change{logs.length === 1 ? '' : 's'} (limit 50).
      </div>
    </OperaWindow>
  );
}
